<template>
  <div v-if="taches.length > 0 || isLoading">
    <UilSpinner
      v-if="isLoading"
      class="tache-spinner spin"
      size="20"
    />
    <ButtonPopover
      v-else
      id="button_taches"
      v-model="open"
      color="primary"
      icon="left"
      placement="bottom"
      trigger="click"
    >
      <template #left-icon>
        <UilClock
          class="context-menu-icon"
          size="16"
        />
        <Counter
          v-if="types.en_cours.nbTaches"
          :value="spaceNumber(types.en_cours.nbTaches)"
          size="small"
        />
      </template>
      <div class="actions-content">
        <div
          v-for="(item, type) in types"
          :key="item.title"
        >
          <strong class="text-regular">
            {{ item.title }}
          </strong>
          <ul>
            <TachePresenter
              v-for="(tache, index) in item.tachesAffichees"
              :key="`${tache.id}_${index}`"
              :tache="tache"
            />
            <li v-if="item.nbTaches === 0">
              {{ $t("tache.aucune-tache-en-cours") }}
            </li>
          </ul>
          <Pagination
            v-model="pageType[type]"
            :total-rows="item.nbTaches"
            :per-page="subPageSize"
          />
        </div>
      </div>
    </ButtonPopover>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import {
  ButtonPopover, Counter, spaceNumber, Pagination,
} from "@lde/core_lde_vue";
import { UilClock, UilSpinner } from "@lde/vue-unicons";

import Tache from "@/domains/taches/tache";
import TachePresenter from "@/components/taches/TachePresenter.ts.vue";

export default defineComponent({
  name: "TacheButton",
  components: {
    ButtonPopover,
    UilClock,
    UilSpinner,
    Counter,
    TachePresenter,
    Pagination,
  },
  data() {
    return {
      open: false,
      isLoading: false,
      pollingInterval: 0,
      pageType: {
        en_cours: 1,
        ko: 1,
        ok: 1,
      } as { [key: string]: number },
      subPageSize: 5, // Nombre de tâches par sous-page
    };
  },
  computed: {
    types() {
      const tachesEnCours = this.taches.filter((tache) => tache.succes == null);
      const tachesKo = this.taches.filter((tache) => tache.succes === false);
      const tachesOk = this.taches.filter((tache) => tache.succes === true);

      const types = {
        en_cours: {
          title: this.$t("tache.taches-en-cours-de-traitement"),
          nbTaches: tachesEnCours.length,
          tachesAffichees: tachesEnCours.slice(
            (this.pageType.en_cours - 1) * this.subPageSize,
            this.pageType.en_cours * this.subPageSize,
          ),
        },
        ko: {
          title: this.$t("tache.taches-necessitant-votre-attention"),
          nbTaches: tachesKo.length,
          tachesAffichees: tachesKo.slice(
            (this.pageType.ko - 1) * this.subPageSize,
            this.pageType.ko * this.subPageSize,
          ),
        },
        ok: {
          title: this.$t("tache.taches-traitees"),
          nbTaches: tachesOk.length,
          tachesAffichees: tachesOk.slice(
            (this.pageType.ok - 1) * this.subPageSize,
            this.pageType.ok * this.subPageSize,
          ),
        },
      };

      return types;
    },
    taches(): Tache[] {
      const taches = this.$store.getters.taches as Tache[];
      return taches.map((tache) => tache.getDetails()).flat();
    },
    sessionExpiree(): boolean {
      return this.$store.getters.sessionExpiree;
    },
  },
  watch: {
    // Le polling s'est arrêté pendant l'expiration : on le relance une fois l'utilisateur reconnecté
    sessionExpiree(expiree: boolean) {
      if (!expiree) {
        this.$store.dispatch("fetchTaches");
        this.startPolling();
      }
    },
  },
  mounted() {
    this.isLoading = true;
    this.$store.dispatch("fetchTaches").finally(() => {
      this.isLoading = false;
    });
    this.startPolling();
  },
  beforeUnmount() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  },
  methods: {
    spaceNumber,
    /**
     * Relève les tâches toutes les minutes ; s'arrête après deux échecs (session expirée, serveur absent).
     */
    startPolling() {
      clearInterval(this.pollingInterval);
      let unauthorizedCount = 0;
      this.pollingInterval = setInterval(() => {
        this.$store.dispatch("fetchTaches").catch(() => {
          unauthorizedCount += 1;
          if (unauthorizedCount >= 2) {
            clearInterval(this.pollingInterval);
          }
        });
      }, 60 * 1000);
    },
  },
});
</script>

<style lang="scss">
@use "@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;

.tache-spinner {
  display: block;

  &.spin {
    animation: spin 1s linear infinite;
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
}

#button_taches {
  .button-classic {
    padding: var.$space-xx-tiny var.$space-small;
    .icon {
      .counter {
        margin-left: var.$space-xx-tiny;
      }
    }
  }

  .popover-container  {
    text-align: center;
    min-width: 50rem;
    border-radius: var.$border-radius-base;
    max-height: calc(100vh - 15rem);
    overflow: auto;
  }

  strong {
    color: var.$blue;
  }

  ul:not(.pagination) {
    margin: var.$space-small;
    list-style: none;
    li + li {
      margin-top: var.$space-xx-tiny;
    }
  }
  .pagination {
    margin-bottom: var.$space-small;
  }
}
</style>
